import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import gamificationService from '../../services/gamificationService'

import type { 
  Badge as SBadge, 
  Achievement as SAchievement, 
  UserProgress as SUserProgress, 
  BadgeProgress as SBadgeProgress, 
  LeaderboardEntry as SLeaderboardEntry 
} from '../../services/gamificationService'

// Badge interface
type Badge = SBadge

// Achievement interface
type Achievement = SAchievement

// User Progress interface
type UserProgress = SUserProgress

// Badge Progress interface
type BadgeProgress = SBadgeProgress

// Leaderboard Entry interface
type LeaderboardEntry = SLeaderboardEntry

// API Base URL
const API_BASE_URL = 'http://localhost:3001/api'

// Gamification API slice
export const gamificationApi = createApi({
  reducerPath: 'gamificationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/gamification`,
    prepareHeaders: (headers, { getState }) => {
      // Auth token ekle
      const token = (getState() as any).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Badge', 'Achievement', 'UserProgress', 'Leaderboard', 'BadgeProgress'],
  endpoints: (builder) => ({
    // Badge endpoints
    getAllBadges: builder.query<Badge[], void>({
      queryFn: async () => {
        try {
          const badges = await gamificationService.getAllBadges()
          return { data: badges }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['Badge'],
    }),

    getBadgesByCategory: builder.query<Badge[], string>({
      query: (category) => `/gamification/badges?category=${category}`,
      providesTags: ['Badge'],
    }),

    getBadgesByRarity: builder.query<Badge[], string>({
      query: (rarity) => `/gamification/badges?rarity=${rarity}`,
      providesTags: ['Badge'],
    }),

    getUserBadges: builder.query<Badge[], string>({
      queryFn: async (userId) => {
        try {
          const allBadges = await gamificationService.getAllBadges()
          return { data: allBadges }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['Badge'],
    }),

    getUnlockedBadges: builder.query<Badge[], string>({
      queryFn: async (userId) => {
        try {
          const unlockedBadges = await gamificationService.getUnlockedBadges(userId)
          return { data: unlockedBadges }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['Badge'],
    }),

    getAvailableBadges: builder.query<Badge[], string>({
      queryFn: async (userId) => {
        try {
          const availableBadges = await gamificationService.getAvailableBadges(userId)
          return { data: availableBadges }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['Badge'],
    }),

    createBadge: builder.mutation<Badge, Omit<Badge, 'id' | 'createdAt' | 'updatedAt'>>({
      query: (badge) => ({
        url: '/gamification/badges',
        method: 'POST',
        body: badge,
      }),
      invalidatesTags: ['Badge'],
    }),

    updateBadge: builder.mutation<Badge, { id: string; updates: Partial<Badge> }>({
      query: ({ id, updates }) => ({
        url: `/gamification/badges/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Badge'],
    }),

    deleteBadge: builder.mutation<void, string>({
      query: (id) => ({
        url: `/gamification/badges/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Badge'],
    }),

    // Badge Progress endpoints
    getBadgeProgress: builder.query<BadgeProgress[], { userId: string; badgeId?: string }>({
      queryFn: async ({ userId }) => {
        try {
          const badgeProgress = await gamificationService.getBadgeProgress(userId)
          return { data: badgeProgress }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['BadgeProgress'],
    }),

    updateBadgeProgress: builder.mutation<
      BadgeProgress,
      { userId: string; badgeId: string; progress: number }
    >({
      query: ({ userId, badgeId, progress }) => ({
        url: `/gamification/users/${userId}/badge-progress/${badgeId}`,
        method: 'PUT',
        body: { progress },
      }),
      invalidatesTags: ['BadgeProgress', 'UserProgress', 'Badge'],
    }),

    unlockBadge: builder.mutation<Badge, { userId: string; badgeId: string }>({
      query: ({ userId, badgeId }) => ({
        url: `/gamification/users/${userId}/badges/${badgeId}/unlock`,
        method: 'POST',
      }),
      invalidatesTags: ['Badge', 'UserProgress', 'BadgeProgress'],
    }),

    // Achievement endpoints
    getAllAchievements: builder.query<Achievement[], void>({
      queryFn: async () => {
        try {
          const achievements = await gamificationService.getAllAchievements()
          return { data: achievements }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['Achievement'],
    }),

    getUserAchievements: builder.query<Achievement[], string>({
      queryFn: async (userId) => {
        try {
          const achievements = await gamificationService.getAllAchievements()
          return { data: achievements }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['Achievement'],
    }),

    getCompletedAchievements: builder.query<Achievement[], string>({
      query: (userId) => `/gamification/users/${userId}/achievements/completed`,
      providesTags: ['Achievement', 'UserProgress'],
    }),

    getAvailableAchievements: builder.query<Achievement[], string>({
      query: (userId) => `/gamification/users/${userId}/achievements/available`,
      providesTags: ['Achievement', 'UserProgress'],
    }),

    updateAchievementProgress: builder.mutation<
      Achievement,
      { userId: string; achievementId: string; progress: number }
    >({
      query: ({ userId, achievementId, progress }) => ({
        url: `/gamification/users/${userId}/achievements/${achievementId}/progress`,
        method: 'PUT',
        body: { progress },
      }),
      invalidatesTags: ['Achievement', 'UserProgress'],
    }),

    completeAchievement: builder.mutation<Achievement, { userId: string; achievementId: string }>({
      query: ({ userId, achievementId }) => ({
        url: `/gamification/users/${userId}/achievements/${achievementId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: ['Achievement', 'UserProgress', 'Badge'],
    }),

    // User Progress endpoints
    getUserProgress: builder.query<UserProgress, string>({
      queryFn: async (userId) => {
        try {
          const progress = await gamificationService.getUserProgress(userId)
          if (!progress) {
            const newProgress = await gamificationService.createUserProgress(userId)
            return { data: newProgress }
          }
          return { data: progress }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['UserProgress'],
    }),

    updateUserProgress: builder.mutation<
      void,
      { userId: string; updates: Partial<UserProgress> }
    >({
      queryFn: async ({ userId, updates }) => {
        try {
          await gamificationService.updateUserProgress(userId, updates)
          return { data: undefined }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      invalidatesTags: ['UserProgress', 'Leaderboard'],
    }),

    addXP: builder.mutation<UserProgress, { userId: string; amount: number; source: string }>({
      query: ({ userId, amount, source }) => ({
        url: `/gamification/users/${userId}/xp`,
        method: 'POST',
        body: { amount, source },
      }),
      invalidatesTags: ['UserProgress'],
    }),

    addCoins: builder.mutation<UserProgress, { userId: string; amount: number; source: string }>({
      query: ({ userId, amount, source }) => ({
        url: `/gamification/users/${userId}/coins`,
        method: 'POST',
        body: { amount, source },
      }),
      invalidatesTags: ['UserProgress'],
    }),

    spendCoins: builder.mutation<UserProgress, { userId: string; amount: number; purpose: string }>({
      query: ({ userId, amount, purpose }) => ({
        url: `/gamification/users/${userId}/coins/spend`,
        method: 'POST',
        body: { amount, purpose },
      }),
      invalidatesTags: ['UserProgress'],
    }),

    // Leaderboard endpoints
    getGlobalLeaderboard: builder.query<
      LeaderboardEntry[],
      { period?: 'daily' | 'weekly' | 'monthly' | 'all'; limit?: number }
    >({
      queryFn: async ({ limit = 50 }) => {
        try {
          const leaderboard = await gamificationService.getLeaderboard(limit)
          return { data: leaderboard }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      providesTags: ['Leaderboard'],
    }),

    getFriendsLeaderboard: builder.query<LeaderboardEntry[], string>({
      query: (userId) => `/gamification/users/${userId}/friends/leaderboard`,
      providesTags: ['Leaderboard'],
    }),

    getClassLeaderboard: builder.query<LeaderboardEntry[], { classId: string; period?: string }>({
      query: ({ classId, period = 'weekly' }) => 
        `/gamification/classes/${classId}/leaderboard?period=${period}`,
      providesTags: ['Leaderboard'],
    }),

    // Statistics endpoints
    getUserStats: builder.query<any, string>({
      query: (userId) => `/gamification/users/${userId}/stats`,
      providesTags: ['UserProgress'],
    }),

    getGlobalStats: builder.query<{
      totalUsers: number
      totalBadgesUnlocked: number
      totalAchievementsCompleted: number
      averageLevel: number
      topPerformers: LeaderboardEntry[]
    }, void>({
      query: () => '/gamification/stats/global',
      providesTags: ['UserProgress', 'Badge', 'Achievement'],
    }),

    // Badge checking and auto-unlock
    checkBadgeEligibility: builder.mutation<string[], string>({
      queryFn: async (userId) => {
        try {
          const newBadges = await gamificationService.checkBadgeEligibility(userId)
          return { data: newBadges }
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } }
        }
      },
      invalidatesTags: ['Badge', 'BadgeProgress'],
    }),

    // Bulk operations
    syncUserProgress: builder.mutation<
      UserProgress,
      { userId: string; activities: Array<{ type: string; value: number; timestamp: string }> }
    >({
      query: ({ userId, activities }) => ({
        url: `/gamification/users/${userId}/sync`,
        method: 'POST',
        body: { activities },
      }),
      invalidatesTags: ['UserProgress', 'Badge', 'Achievement', 'BadgeProgress'],
    }),
  }),
})

// Export hooks
export const {
  // Badge hooks
  useGetAllBadgesQuery,
  useGetBadgesByCategoryQuery,
  useGetBadgesByRarityQuery,
  useGetUserBadgesQuery,
  useGetUnlockedBadgesQuery,
  useGetAvailableBadgesQuery,
  useCreateBadgeMutation,
  useUpdateBadgeMutation,
  useDeleteBadgeMutation,

  // Badge Progress hooks
  useGetBadgeProgressQuery,
  useUpdateBadgeProgressMutation,
  useUnlockBadgeMutation,

  // Achievement hooks
  useGetAllAchievementsQuery,
  useGetUserAchievementsQuery,
  useGetCompletedAchievementsQuery,
  useGetAvailableAchievementsQuery,
  useUpdateAchievementProgressMutation,
  useCompleteAchievementMutation,

  // User Progress hooks
  useGetUserProgressQuery,
  useUpdateUserProgressMutation,
  useAddXPMutation,
  useAddCoinsMutation,
  useSpendCoinsMutation,

  // Leaderboard hooks
  useGetGlobalLeaderboardQuery,
  useGetFriendsLeaderboardQuery,
  useGetClassLeaderboardQuery,

  // Statistics hooks
  useGetUserStatsQuery,
  useGetGlobalStatsQuery,

  // Utility hooks
  useCheckBadgeEligibilityMutation,
  useSyncUserProgressMutation,
} = gamificationApi

export default gamificationApi